import { useCallback, useEffect, useMemo, useState } from "react"
import ReCaptcha from "react-google-recaptcha"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import axios from "axios"
import { Button } from "@inkblot-therapy/squid"
import {
  useSectionDispatch,
  useSectionState,
} from "../../context/SectionContextProvider"
import { useDialogConfig } from "../../hooks/useDialogConfig"
import { useGetFormByProvince } from "../../hooks/useGetFormByProvince"
import { usePostReferralData } from "../../hooks/usePostReferralData"
import { usePrevious } from "../../hooks/usePrevious"
import {
  splitProvinceString,
  toTitleCase,
} from "../../utils/utilityFunctions/utilityFunction"
import { FormQuestion } from "../../types/FormQuestion"
import { FormSection as FormSectionType } from "../../types/FormSection"
import { ReferralPayload } from "../../types/ReferralPayload"
import { Section } from "../../types/SectionState"
import { Alert } from "../Alert"
import { Dialog } from "../Dialog"
import { FormReviewPage } from "../FormReviewPage"
import { FormSection } from "../FormSection"
import { Toast } from "../../types/ToastState"
import { useToastDispatch } from "../../context/ToastContextProvider"
import { EnvironmentService } from "../../services/EnvironmentService"

const getQuestionsBySection = (formSection?: FormSectionType) => {
  const childQuestions: FormQuestion[] = []
  const parentQuestions =
    formSection?.form_rows
      ?.flatMap((question) => question.form_questions)
      .map((question) => {
        if (question.nested_question) {
          childQuestions.push(question.nested_question)
        }
        return question
      }) || []
  return [...parentQuestions, ...childQuestions]
}

const getSectionData = (order: number = 0, data?: FormSectionType[]) => {
  return data?.[order - 1]
}

export function Form() {
  const methods = useFormContext()
  const navigate = useNavigate()
  const { step, province } = useParams<{ step: string; province: string }>()
  const { data } = useGetFormByProvince(province ?? "ontario")
  const dispatch = useSectionDispatch()
  const sections = useSectionState()
  const { t } = useTranslation()
  const { postReferralData } = usePostReferralData()
  const [token, setToken] = useState("")
  const config = useDialogConfig()
  const toastDispatch = useToastDispatch()

  var recaptchaSiteKey = EnvironmentService.getReCaptchaSiteKey()

  const order = Number(step)
  const previousOrder = usePrevious(order)
  const currentSectionData = getSectionData(order, data)
  const previousSectionData = getSectionData(previousOrder, data)
  const currentQuestions = getQuestionsBySection(currentSectionData)
  const previousQuestions = getQuestionsBySection(previousSectionData)
  const isReviewPage = order === (data?.length ?? 0) + 1
  const isRecaptchaRequired = EnvironmentService.getEnv() === "production"
  const hasIncompleteSections = useMemo(() => {
    const removeReview = ({ name }: Section) =>
      name !== t("client.sideNav.review")
    const removeConditionalSections = ({ conditional }: Section) => !conditional
    const checkCompletion = ({ completed }: Section) => !completed
    return Object.values(sections)
      .filter(removeReview)
      .filter(removeConditionalSections)
      .some(checkCompletion)
  }, [sections, t])

  const triggerValidation = useCallback(
    (questions: FormQuestion[]) => {
      const fieldNames = questions.map(({ mapped_field }) => mapped_field)
      methods.trigger(fieldNames)
    },
    [methods],
  )

  useEffect(() => {
    if (order !== previousOrder && previousQuestions) {
      triggerValidation(previousQuestions)
    }
  }, [data, previousQuestions, triggerValidation, order, previousOrder])

  const resetForm = () => {
    sessionStorage.clear()
    methods.reset({})
    dispatch({ type: "RESET_SECTIONS" })
  }

  const onFormClear = () => {
    config.openDialog({
      description: t("client.form.dialogs.clearForm.description"),
      title: t("client.form.dialogs.clearForm.title"),
      cancelText: t("client.form.dialogs.clearForm.cancelText"),
      confirmText: t("client.form.dialogs.clearForm.confirmText"),
      variant: "confirmation",
      onConfirm: () => {
        resetForm()
        navigate(`/${province}`)
      },
    })
  }

  const postReferralDataWrapper = (
    values: ReferralPayload,
    province: string,
  ) => {
    return new Promise((resolve, reject) => {
      const referralData = { ...values, "g-recaptcha-response": token }
      const variables = { province, referralData }
      postReferralData(variables, {
        onSuccess: (data) => {
          resolve(data)
          if (sessionStorage.getItem("origin") === "admin") {
            navigate(`/manage/${data.id}`)
            toastDispatch({
              type: "ADD_TOAST",
              toast: {
                status: "success",
                header: t("admin.labels.successSaveHeader"),
                text: t("admin.labels.successReferral"),
              } as Toast,
            })
          } else {
            navigate(`../${step}?status=success&id=${data.client_id}`)
          }
          resetForm()
        },
        onError: (err) => {
          const errorMessage = err.response?.data?.error
          const error = new Error(errorMessage || err.message)
          if (axios.isAxiosError(err)) {
            navigate(
              `../${step}?status=error&error=${errorMessage || err.message}`,
            )
          } else {
            navigate(`../${step}?status=error`)
          }
          toastDispatch({
            type: "ADD_TOAST",
            toast: {
              status: "critical",
              header: t("client.submission.error.toastTitle"),
              text: err.message.toString(),
            } as Toast,
          })
          reject(error)
        },
      })
    })
  }

  const handleSubmit = () => {
    const origin = sessionStorage.getItem("origin")
    methods.handleSubmit(async (values) => {
      const newValues = {
        ...values,
        referral_submission_type: origin === "admin" ? "fax" : "online",
        client_province: toTitleCase(splitProvinceString(province)),
      }
      try {
        if (!province) {
          throw new Error(t("client.submission.error.missingProvince"))
        }
        await postReferralDataWrapper(newValues, province)
      } catch (err) {
        console.log(err)
      }
    })()
  }

  const onSubmit = () => {
    if (isReviewPage) {
      // in some cases isValid is false even when the errors object is empty
      // thus we are checking to see if any errors exist in the object
      if (Object.values(methods.formState.errors).length) {
        config.openDialog({
          title: t("client.form.dialogs.error.title"),
          description: t("client.form.dialogs.error.description"),
          confirmText: t("client.form.dialogs.error.confirmText"),
          variant: "alert",
        })
      } else if (hasIncompleteSections) {
        config.openDialog({
          description: t("client.form.dialogs.confirm.description"),
          cancelText: t("client.form.dialogs.confirm.cancelText"),
          confirmText: t("client.form.dialogs.confirm.confirmText"),
          title: t("client.form.dialogs.confirm.title"),
          variant: "confirmation",
          onConfirm: handleSubmit,
        })
      } else {
        handleSubmit()
      }
    } else {
      currentQuestions && triggerValidation(currentQuestions)
      skipConditionalSection("next")
        ? navigate("../" + String(order + 2))
        : navigate("../" + String(order + 1))
    }
  }

  const skipConditionalSection = (action: string) => {
    const sectionData =
      action === "previous"
        ? getSectionData(order - 1, data)
        : getSectionData(order + 1, data)
    if (sectionData) {
      const { conditional_section_mapped_field, conditional_section_values } =
        sectionData
      if (conditional_section_mapped_field && conditional_section_values) {
        const values = methods.getValues(conditional_section_mapped_field)
        if (
          conditional_section_values &&
          !conditional_section_values.includes(values)
        )
          return true
      }
    }
    return false
  }

  const buttonText = isReviewPage
    ? t("client.buttons.submitForm")
    : t("client.buttons.next")

  return (
    <>
      <Dialog {...config} />
      {isReviewPage ? <FormReviewPage /> : null}
      {data?.map((formSection) => {
        return (
          <FormSection
            key={formSection.section_name}
            section={formSection}
            isHidden={formSection.order !== order}
          />
        )
      })}
      {isReviewPage && (
        <div id="pleasePrint" className="flex mb-4 w-full">
          <Alert
            status="neutral"
            size="lg"
            text={t("client.review.alerts.printRecord")}
            heading
          >
            {t("client.review.alerts.printInstructions")}
          </Alert>
        </div>
      )}
      <div className="flex justify-end mb-4">
        {isRecaptchaRequired && (
          <ReCaptcha
            onChange={(token) => token && setToken(token)}
            hidden={!isReviewPage}
            sitekey={recaptchaSiteKey ?? ""}
          />
        )}
      </div>
      <div className="flex justify-between">
        <div className="flex w-full">
          <Button variant="critical" onClick={onFormClear}>
            {t("client.buttons.clearForm")}
          </Button>
        </div>
        <div className="flex justify-end w-full flex-row">
          {order !== 1 ? (
            <div className="mr-4 inline-block">
              <Button
                variant="outlined"
                onClick={() => {
                  currentQuestions && triggerValidation(currentQuestions)
                  skipConditionalSection("previous")
                    ? navigate("../" + String(order - 2))
                    : navigate("../" + String(order - 1))
                }}
                leftIcon="ArrowLeft"
              >
                {t("client.buttons.previous")}
              </Button>
            </div>
          ) : null}
          <Button
            disabled={isRecaptchaRequired && !token && isReviewPage}
            variant="primary"
            onClick={onSubmit}
            rightIcon="ArrowRight"
          >
            {buttonText}
          </Button>
        </div>
      </div>
    </>
  )
}
